import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import { ArrowLeft, BookOpen, FileText, Pencil } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useJournal, useUpdateJournal } from "~/hooks/contabilidad/useJournals";
import { useMovements } from "~/hooks/contabilidad/useMovements";
import type { JournalFormData } from "~/types/contabilidad";
import { JOURNAL_TYPE_LABELS } from "~/types/contabilidad";

import { DiarioDialog } from "./components/DiarioDialog";

export default function DiarioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: journal, isLoading } = useJournal(id!);
  const { data: movements = [] } = useMovements({ journal_id: id });
  const updateMutation = useUpdateJournal();

  const handleUpdate = (data: JournalFormData) => {
    updateMutation.mutate(
      { id: id!, data },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto w-[80vw] space-y-6 py-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="container mx-auto w-[80vw] py-6">
        <p className="text-muted-foreground">Diario no encontrado.</p>
        <Button
          variant="link"
          onClick={() => navigate("/contabilidad/diarios")}
          className="mt-4"
        >
          Volver a la lista
        </Button>
      </div>
    );
  }

  const totalDebit = movements.reduce((sum, m) => {
    // Note: We would need to fetch lines to calculate this accurately
    return sum;
  }, 0);

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/contabilidad/diarios")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <BookOpen className="h-6 w-6" />
              {journal.name}
            </h1>
            <p className="text-muted-foreground">
              Código: {journal.code} • Creado el{" "}
              {dayjs(journal.created_at).format("DD/MM/YYYY")}
            </p>
          </div>
        </div>
        <Button
          onClick={() => setEditDialogOpen(true)}
          className="rounded-full"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tipo</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge variant="outline" className="text-lg">
              {JOURNAL_TYPE_LABELS[journal.type]}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Movimientos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{movements.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList>
          <TabsTrigger value="info">Información</TabsTrigger>
          <TabsTrigger value="movimientos">Movimientos</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Código</p>
                  <p className="font-mono font-medium">{journal.code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Nombre</p>
                  <p className="font-medium">{journal.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <Badge variant="outline">
                    {JOURNAL_TYPE_LABELS[journal.type]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Empresa</p>
                  <p
                    className="cursor-pointer font-medium text-primary hover:underline"
                    onClick={() =>
                      navigate(`/contabilidad/empresas/${journal.company_id}`)
                    }
                  >
                    {journal.company?.name ?? "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Fecha de Creación
                  </p>
                  <p className="font-medium">
                    {dayjs(journal.created_at).format("DD/MM/YYYY HH:mm")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movimientos">
          <Card>
            <CardHeader>
              <CardTitle>Movimientos del Diario</CardTitle>
            </CardHeader>
            <CardContent>
              {movements.length === 0 ? (
                <p className="text-muted-foreground">
                  No hay movimientos registrados en este diario.
                </p>
              ) : (
                <div className="space-y-2">
                  {movements.map((movement) => (
                    <div
                      key={movement.id}
                      className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-muted/50"
                      onClick={() =>
                        navigate(`/contabilidad/movimientos/${movement.id}`)
                      }
                    >
                      <div>
                        <p className="font-medium">{movement.ref}</p>
                        <p className="text-sm text-muted-foreground">
                          {dayjs(movement.move_date).format("DD/MM/YYYY")}
                        </p>
                      </div>
                      <Badge
                        variant={
                          movement.is_published ? "default" : "secondary"
                        }
                      >
                        {movement.is_published ? "Publicado" : "Borrador"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <DiarioDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        journal={journal}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
}
